import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PrivateChat.css';

const API = 'https://cms.amservices.eu/api/private_chat.php';

async function request(path, body) {
  let user;
  try { user = JSON.parse(localStorage.getItem('am_user') || 'null'); } catch { /* session invalide */ }
  const token = localStorage.getItem('am_token') || user?.token || '';
  const response = await fetch(`${API}${path}`, {
    method: body ? 'POST' : 'GET',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.message || 'Messagerie indisponible.');
  return data;
}

export default function PrivateChat() {
  const [searchParams] = useSearchParams();
  const requestedUserId = Number(searchParams.get('user_id'));
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selected?.id;

  useEffect(() => {
    let active = true;
    request('?action=users').then(data => {
      if (!active) return;
      setMe(data.me);
      setUsers(data.users);
      setSelected(current => current || data.users[0] || null);
    }).catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const contact = users.find(person => Number(person.id) === requestedUserId);
    if (contact) setSelected(contact);
  }, [requestedUserId, users]);

  useEffect(() => {
    if (!selected) { setMessages([]); return undefined; }
    let active = true;
    let inFlight = false;
    setMessages([]);
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await request(`?action=messages&user_id=${selected.id}`);
        if (active) {
          setMessages(current => {
            const latestServerId = Number(data.messages.at(-1)?.id || 0);
            const newerLocal = current.filter(item => Number(item.id) > latestServerId);
            const next = [...data.messages, ...newerLocal];
            return current.length === next.length && current.every((item, index) => String(item.id) === String(next[index].id)) ? current : next;
          });
          setError('');
        }
      } catch (err) { if (active) setError(err.message); }
      finally { inFlight = false; }
    };
    load();
    const timer = window.setInterval(load, 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selected]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages]);

  const send = async event => {
    event.preventDefault();
    const message = draft.trim();
    if (!selected || !message || sending) return;
    const recipientId = Number(selected.id);
    setSending(true);
    setError('');
    try {
      const data = await request('', { recipient_id: recipientId, message });
      setDraft('');
      if (Number(selectedIdRef.current) === recipientId) {
        setMessages(current => current.some(item => String(item.id) === String(data.id)) ? current : [...current, {
          id: data.id,
          sender_id: me?.id,
          recipient_id: recipientId,
          message,
          created_at: new Date().toISOString(),
        }]);
      }
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  const filtered = users.filter(user => `${user.nom || ''} ${user.login}`.toLowerCase().includes(search.toLowerCase()));

  return <main className="private-chat">
    <header className="private-chat-header"><div><span>Messagerie interne</span><h1>Chat privé</h1><p>Échangez directement avec un membre de l’équipe.</p></div><span className="private-chat-self">{me?.nom || me?.login || ''}</span></header>
    {error && <div className="private-chat-error" role="alert">{error}</div>}
    <div className="private-chat-layout">
      <aside className="private-chat-people" aria-label="Contacts">
        <label htmlFor="private-chat-search">Contacts</label>
        <input id="private-chat-search" type="search" placeholder="Rechercher une personne" value={search} onChange={event => setSearch(event.target.value)} />
        <div className="private-chat-people-list">
          {loading && <p>Chargement des contacts…</p>}
          {!loading && !filtered.length && <p>Aucun contact trouvé.</p>}
          {filtered.map(user => <button key={user.id} type="button" className={selected?.id === user.id ? 'active' : ''} onClick={() => setSelected(user)}>
            <span className="private-chat-avatar">{(user.nom || user.login).slice(0, 1).toUpperCase()}</span>
            <span><strong>{user.nom || user.login}</strong><small>@{user.login}</small></span>
          </button>)}
        </div>
      </aside>
      <section className="private-chat-conversation" aria-label="Conversation privée">
        {selected ? <>
          <div className="private-chat-conversation-title"><span className="private-chat-avatar">{(selected.nom || selected.login).slice(0, 1).toUpperCase()}</span><div><strong>{selected.nom || selected.login}</strong><small>Conversation privée</small></div></div>
          <div className="private-chat-messages" aria-live="polite">
            {!messages.length && <p className="private-chat-empty">Aucun message. Commencez la conversation.</p>}
            {messages.map(item => <div key={item.id} className={`private-chat-bubble ${Number(item.sender_id) === Number(me?.id) ? 'mine' : ''}`}><p>{item.message}</p><time>{new Date(item.created_at.replace(' ', 'T')).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</time></div>)}
            <div ref={endRef} />
          </div>
          <form className="private-chat-compose" onSubmit={send}>
            <label htmlFor="private-chat-draft" className="sr-only">Votre message</label>
            <textarea id="private-chat-draft" rows="2" maxLength="4000" placeholder="Écrire un message…" value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form.requestSubmit(); } }} />
            <button type="submit" disabled={!draft.trim() || sending}>{sending ? 'Envoi…' : 'Envoyer'}</button>
          </form>
        </> : <p className="private-chat-empty">Choisissez un contact pour commencer.</p>}
      </section>
    </div>
  </main>;
}
